// i18n content for the informational sections (Legal/Tax, Incentives, Land & Leasing,
// Sector Opportunities, Updates) and the Privacy / Terms pages, across all 8 languages.
// Merged into the base translations at runtime. Proper nouns and codes
// (Fiji, Nadi, Suva, TIN, VAT, TLTB, FRCS, RBF, MCTTT, BPO) stay untranslated.
// Numeric dates ("06 / 2026") are intentionally language-neutral.

export const sections = {
  en: {
    legalCommon: { legal: "Legal", back: "Back" },
    legal: {
      overline: "Legal & regulatory essentials",
      title: "The rules, in plain terms.",
      reviewed: "Reviewed June 2026",
      items: [
        { k: "Corporate Income Tax", v: "25%" },
        { k: "VAT", v: "12.5%" },
        { k: "Withholding Tax", v: "Confirmed at engagement — subject to the latest FRCS guidance." },
        { k: "Reserve Sectors & Ownership", v: "Industry ownership caps and compliance steps vary by sector." },
        { k: "Repatriation & Exchange Control", v: "Managed via Reserve Bank of Fiji (RBF) supervision." },
      ],
      disclaimer: "Tax figures are general guidance and are subject to the latest Fiji Revenue & Customs Service (FRCS) guidance. Exact rates and obligations are confirmed at engagement.",
    },
    incentives: {
      overline: "Investment incentives",
      title: "Sector incentives worth knowing.",
      reviewed: "Incentives verified at engagement; overview last reviewed June 2026",
      items: [
        { t: "Tourism & Hospitality", d: "Duty concessions, accelerated depreciation, and sector-specific grants." },
        { t: "Clean Energy & Agribusiness", d: "Tax holidays, land lease facilitation, and green finance partnerships." },
        { t: "Technology & BPO", d: "Skills development subsidies and special economic zone benefits." },
      ],
    },
    land: {
      overline: "Land & leasing",
      title: "How land ownership and leasing works in Fiji.",
      sub: "Foreign investors operate with confidence once they understand Fiji's land system. Here is the essential picture — and the steps to secure a lease.",
      typesHeading: "Land types & ownership caps",
      types: [
        { t: "iTaukei (native) land", d: "About 90% of Fiji's land. It cannot be sold — only leased, typically through the iTaukei Land Trust Board (TLTB)." },
        { t: "State land", d: "Government-owned land leased via the Department of Lands. Common for commercial and industrial sites." },
        { t: "Freehold land", d: "A small share (~8%) that can be bought and sold. Foreign buyers face limits on residential freehold near town boundaries." },
        { t: "Foreign ownership caps", d: "Foreigners generally cannot own residential land within municipal boundaries; commercial and investment leases remain open with approval." },
      ],
      stepsHeading: "Securing a lease — four steps",
      steps: [
        { t: "Identify the land type", d: "Confirm whether the site is iTaukei, State or freehold — this sets the entire approval path." },
        { t: "Secure consent", d: "TLTB consent for iTaukei land, or Director of Lands approval for State land." },
        { t: "Negotiate the lease", d: "Agree term, premium and annual rent; commercial leases commonly run 30–99 years." },
        { t: "Register the lease", d: "Register with the Registrar of Titles so your interest is legally protected." },
      ],
    },
    sectors: {
      overline: "Sector opportunities & success stories",
      title: "Real setups, real sectors.",
      sub: "A snapshot of investors we've guided from first enquiry to an operating company in Fiji.",
      note: "Illustrative of typical client outcomes.",
      stories: [
        { sector: "Tourism", quote: "They mapped every permit before we flew in. Our resort company was registered and banking-ready in weeks.", name: "Boutique resort investor", detail: "Registered remotely · banking finalised on one trip" },
        { sector: "Agribusiness", quote: "The land-lease guidance alone saved us months. We knew exactly which consents we needed.", name: "Export agribusiness founder", detail: "TLTB lease secured · export licensing complete" },
        { sector: "Technology & BPO", quote: "As a first-time foreign founder I expected chaos. Instead it was one team, one clear path.", name: "BPO services director", detail: "8-step setup · work permits for 12 staff" },
      ],
    },
    updates: {
      overline: "Updates",
      title: "Recent policy & regulatory notes.",
      sub: "A short, plain-English feed of changes worth knowing.",
      note: "General information, not advice. Confirm current requirements with us before acting.",
      items: [
        { date: "06 / 2026", tag: "Tax", text: "FRCS continues phased digital lodgement for VAT — most filings are now online." },
        { date: "05 / 2026", tag: "Investment", text: "Investment Fiji reaffirmed sector priorities in tourism, renewable energy and agribusiness." },
        { date: "04 / 2026", tag: "Immigration", text: "Investor permit processing is holding around 21 working days on complete applications." },
        { date: "03 / 2026", tag: "Banking", text: "Corporate account onboarding still requires one in-person visit; document checklists updated." },
      ],
    },
    privacy: {
      title: "Privacy Policy",
      updated: "June 2026",
      clauses: [
        { h: "Who we are", body: ["Alvin Kumar & Associates (trading as AK Fiji) is a registered advisory firm with offices in Nadi and Suva, Fiji. This policy explains how we collect, use and protect the personal information you share with us through this website."] },
        { h: "Information we collect", body: ["When you request our free guide we collect the details you submit: your full name, email address, phone number and, optionally, your location. We also record whether you consented to receive our communications."] },
        { h: "How we use your information", body: ["We use your information to send you the guide you requested and related follow-up materials, respond to your enquiries about setting up a business in Fiji, and provide quotes and consultation scheduling when you ask for them. We do not sell your personal information to third parties."] },
        { h: "Data retention & security", body: ["Your details are stored securely and retained only for as long as needed to provide our services and to comply with applicable record-keeping obligations. Access is restricted to authorised team members."] },
        { h: "Your rights", body: ["You may request access to, correction of, or deletion of your personal data, and you can opt out of our communications at any time by contacting us."] },
        { h: "Cookies", body: ["We use essential cookies to make this site work and to understand aggregate traffic. You can accept or decline non-essential cookies via the banner shown on your first visit."] },
      ],
      contactHeading: "Contact",
      contactLead: "Questions about this policy? Reach us at:",
    },
    terms: {
      title: "Terms of Service",
      updated: "June 2026",
      clauses: [
        { h: "Acceptance of terms", body: ["By using this website and requesting our free guide, you agree to these terms. If you do not agree, please do not use the site."] },
        { h: "Nature of information", body: ["The guide, checklists, timelines, fees and tax figures provided on this site are general information only. They are not legal, tax or financial advice, and rates and requirements are subject to the latest guidance from the Fiji Revenue & Customs Service (FRCS) and other authorities. Exact obligations are confirmed at engagement."] },
        { h: "No guarantee of outcome", body: ["While we draw on real experience setting up businesses in Fiji, timelines and approvals depend on third-party authorities and your specific circumstances. We do not guarantee any particular outcome or processing time."] },
        { h: "Your responsibilities", body: ["You agree to provide accurate information and to use the materials for your own lawful business planning. You are responsible for verifying current requirements before making decisions."] },
        { h: "Limitation of liability", body: ["To the fullest extent permitted by law, Alvin Kumar & Associates (trading as AK Fiji) is not liable for any loss arising from reliance on the general information provided on this website."] },
      ],
      contactHeading: "Contact",
      contactLead: "Questions about these terms? Reach us at:",
    },
  },

  zh: {
    legalCommon: { legal: "法律", back: "返回" },
    legal: {
      overline: "法律与监管要点",
      title: "用通俗的语言说清规则。",
      reviewed: "2026年6月审核",
      items: [
        { k: "公司所得税", v: "25%" },
        { k: "VAT 增值税", v: "12.5%" },
        { k: "预扣税", v: "在正式合作时确认——以 FRCS 最新指引为准。" },
        { k: "限制性行业与持股", v: "各行业的持股上限和合规步骤因行业而异。" },
        { k: "利润汇回与外汇管制", v: "由斐济储备银行（RBF）监管。" },
      ],
      disclaimer: "税务数据仅为一般指引，以斐济税务与海关局（FRCS）的最新指引为准。确切税率与义务在正式合作时确认。",
    },
    incentives: {
      overline: "投资激励",
      title: "值得了解的行业激励。",
      reviewed: "激励在正式合作时核实；概览最后审核于 2026年6月",
      items: [
        { t: "旅游与酒店业", d: "关税减免、加速折旧以及行业专项补助。" },
        { t: "清洁能源与农业综合企业", d: "免税期、土地租赁便利以及绿色金融合作。" },
        { t: "科技与 BPO", d: "技能发展补贴以及经济特区优惠。" },
      ],
    },
    land: {
      overline: "土地与租赁",
      title: "斐济的土地所有权与租赁如何运作。",
      sub: "只要了解斐济的土地制度，外国投资者便可安心运营。以下是核心概况，以及取得租约的步骤。",
      typesHeading: "土地类型与持有上限",
      types: [
        { t: "iTaukei（原住民）土地", d: "约占斐济土地的 90%。不可出售——仅可租赁，通常通过 iTaukei 土地信托委员会（TLTB）办理。" },
        { t: "国有土地", d: "政府所有，通过土地部租赁。常用于商业和工业用地。" },
        { t: "永久业权土地", d: "占比较小（约 8%），可买卖。外国买家在城镇边界附近的住宅永久业权方面受限。" },
        { t: "外资持有上限", d: "外国人一般不得拥有市政边界内的住宅用地；商业与投资租赁在获批后仍可办理。" },
      ],
      stepsHeading: "取得租约——四个步骤",
      steps: [
        { t: "确认土地类型", d: "确认地块属于 iTaukei、国有还是永久业权——这决定了整个审批路径。" },
        { t: "取得同意", d: "iTaukei 土地需 TLTB 同意，国有土地需土地署长批准。" },
        { t: "商议租约", d: "就期限、租赁溢价和年租金达成一致；商业租约通常为 30–99 年。" },
        { t: "登记租约", d: "在产权登记处登记，使您的权益受法律保护。" },
      ],
    },
    sectors: {
      overline: "行业机会与成功案例",
      title: "真实的设立，真实的行业。",
      sub: "我们指导过的投资者从首次咨询到在斐济成立运营公司的缩影。",
      note: "为典型客户成果的示意说明。",
      stories: [
        { sector: "旅游业", quote: "在我们抵达之前，他们已梳理好每一项许可。我们的度假村公司在数周内完成注册并可开户。", name: "精品度假村投资者", detail: "远程注册 · 一次行程完成开户" },
        { sector: "农业综合企业", quote: "仅土地租赁的指导就为我们节省了数月时间。我们清楚地知道需要哪些同意。", name: "出口型农业创始人", detail: "取得 TLTB 租约 · 完成出口许可" },
        { sector: "科技与 BPO", quote: "作为首次来斐济的外国创始人，我本以为会很混乱。结果却是一支团队、一条清晰路径。", name: "BPO 服务总监", detail: "八步设立 · 为 12 名员工办理工作许可" },
      ],
    },
    updates: {
      overline: "最新动态",
      title: "近期政策与监管要点。",
      sub: "值得关注的变化，以简明语言呈现的简短动态。",
      note: "此为一般信息，非专业意见。行动前请与我们确认现行要求。",
      items: [
        { date: "06 / 2026", tag: "税务", text: "FRCS 持续推进 VAT 的分阶段电子申报——多数申报现已在线办理。" },
        { date: "05 / 2026", tag: "投资", text: "Investment Fiji 重申旅游、可再生能源和农业综合企业的行业优先方向。" },
        { date: "04 / 2026", tag: "移民", text: "对于材料完整的申请，投资者许可的办理时间保持在约 21 个工作日。" },
        { date: "03 / 2026", tag: "银行", text: "企业账户开户仍需一次亲临；文件清单已更新。" },
      ],
    },
    privacy: {
      title: "隐私政策",
      updated: "2026年6月",
      clauses: [
        { h: "关于我们", body: ["Alvin Kumar & Associates (trading as AK Fiji) 是一家持牌顾问公司，在斐济楠迪和苏瓦设有办公室。本政策说明我们如何收集、使用和保护您通过本网站分享的个人信息。"] },
        { h: "我们收集的信息", body: ["当您索取免费指南时，我们会收集您提交的信息：全名、电子邮箱、电话号码，以及（可选的）所在地。我们也会记录您是否同意接收我们的通讯。"] },
        { h: "我们如何使用您的信息", body: ["我们使用您的信息向您发送所索取的指南及相关后续资料，回复您关于在斐济设立企业的咨询，并在您请求时提供报价和咨询预约。我们不会将您的个人信息出售给第三方。"] },
        { h: "数据保留与安全", body: ["您的信息将被安全存储，仅在提供服务所需及遵守相关记录保存义务的期限内保留。访问权限仅限授权团队成员。"] },
        { h: "您的权利", body: ["您可以随时请求访问、更正或删除您的个人数据，也可随时联系我们退订通讯。"] },
        { h: "Cookie", body: ["我们使用必要的 Cookie 以保证网站运行并了解总体访问情况。您可通过首次访问时显示的横幅接受或拒绝非必要 Cookie。"] },
      ],
      contactHeading: "联系我们",
      contactLead: "对本政策有疑问？请通过以下方式联系我们：",
    },
    terms: {
      title: "服务条款",
      updated: "2026年6月",
      clauses: [
        { h: "条款的接受", body: ["使用本网站并索取我们的免费指南，即表示您同意这些条款。如您不同意，请勿使用本网站。"] },
        { h: "信息性质", body: ["本网站提供的指南、清单、时间安排、费用和税务数据仅为一般信息。它们不构成法律、税务或财务意见；税率与要求以斐济税务与海关局（FRCS）及其他主管部门的最新指引为准。确切义务在正式合作时确认。"] },
        { h: "不保证结果", body: ["尽管我们凭借在斐济设立企业的真实经验提供服务，但时间安排与审批取决于第三方主管部门及您的具体情况。我们不保证任何特定结果或办理时间。"] },
        { h: "您的责任", body: ["您同意提供准确信息，并将这些资料用于您自身合法的业务规划。在作出决定前，核实现行要求是您的责任。"] },
        { h: "责任限制", body: ["在法律允许的最大范围内，Alvin Kumar & Associates (trading as AK Fiji) 对因依赖本网站所提供的一般信息而产生的任何损失概不负责。"] },
      ],
      contactHeading: "联系我们",
      contactLead: "对本条款有疑问？请通过以下方式联系我们：",
    },
  },

  hi: {
    legalCommon: { legal: "कानूनी", back: "वापस" },
    legal: {
      overline: "कानूनी और नियामक अनिवार्यताएँ",
      title: "नियम, सरल शब्दों में।",
      reviewed: "जून 2026 में समीक्षित",
      items: [
        { k: "कॉर्पोरेट आयकर", v: "25%" },
        { k: "VAT", v: "12.5%" },
        { k: "विदहोल्डिंग टैक्स", v: "जुड़ाव के समय पुष्टि की जाती है — नवीनतम FRCS मार्गदर्शन के अधीन।" },
        { k: "आरक्षित क्षेत्र व स्वामित्व", v: "उद्योग स्वामित्व सीमाएँ और अनुपालन चरण क्षेत्र के अनुसार भिन्न होते हैं।" },
        { k: "प्रत्यावर्तन व विनिमय नियंत्रण", v: "रिज़र्व बैंक ऑफ़ फ़िजी (RBF) की निगरानी में प्रबंधित।" },
      ],
      disclaimer: "कर आंकड़े सामान्य मार्गदर्शन हैं और फ़िजी राजस्व एवं सीमा शुल्क सेवा (FRCS) के नवीनतम मार्गदर्शन के अधीन हैं। सटीक दरें और दायित्व जुड़ाव के समय पुष्ट होते हैं।",
    },
    incentives: {
      overline: "निवेश प्रोत्साहन",
      title: "जानने योग्य क्षेत्रीय प्रोत्साहन।",
      reviewed: "प्रोत्साहन जुड़ाव के समय सत्यापित; अवलोकन अंतिम बार जून 2026 में समीक्षित",
      items: [
        { t: "पर्यटन व आतिथ्य", d: "शुल्क रियायतें, त्वरित मूल्यह्रास और क्षेत्र-विशिष्ट अनुदान।" },
        { t: "स्वच्छ ऊर्जा व कृषि-व्यवसाय", d: "कर अवकाश, भूमि पट्टा सुविधा और हरित वित्त साझेदारियाँ।" },
        { t: "प्रौद्योगिकी व BPO", d: "कौशल विकास सब्सिडी और विशेष आर्थिक क्षेत्र लाभ।" },
      ],
    },
    land: {
      overline: "भूमि व पट्टा",
      title: "फ़िजी में भूमि स्वामित्व और पट्टा कैसे काम करता है।",
      sub: "फ़िजी की भूमि प्रणाली समझ लेने पर विदेशी निवेशक आत्मविश्वास से काम करते हैं। यहाँ मूल तस्वीर और पट्टा हासिल करने के चरण हैं।",
      typesHeading: "भूमि के प्रकार व स्वामित्व सीमाएँ",
      types: [
        { t: "iTaukei (मूल) भूमि", d: "फ़िजी की लगभग 90% भूमि। इसे बेचा नहीं जा सकता — केवल पट्टे पर दिया जाता है, आमतौर पर iTaukei लैंड ट्रस्ट बोर्ड (TLTB) के माध्यम से।" },
        { t: "राज्य भूमि", d: "सरकारी स्वामित्व वाली भूमि, भूमि विभाग के माध्यम से पट्टे पर। वाणिज्यिक व औद्योगिक स्थलों के लिए सामान्य।" },
        { t: "फ़्रीहोल्ड भूमि", d: "एक छोटा हिस्सा (~8%) जिसे खरीदा-बेचा जा सकता है। नगर सीमाओं के पास आवासीय फ़्रीहोल्ड पर विदेशी खरीदारों को सीमाएँ हैं।" },
        { t: "विदेशी स्वामित्व सीमाएँ", d: "विदेशी आमतौर पर नगरपालिका सीमाओं के भीतर आवासीय भूमि के मालिक नहीं हो सकते; वाणिज्यिक व निवेश पट्टे स्वीकृति के साथ उपलब्ध रहते हैं।" },
      ],
      stepsHeading: "पट्टा हासिल करना — चार चरण",
      steps: [
        { t: "भूमि का प्रकार पहचानें", d: "पुष्टि करें कि स्थल iTaukei, राज्य या फ़्रीहोल्ड है — यही पूरा अनुमोदन मार्ग तय करता है।" },
        { t: "सहमति प्राप्त करें", d: "iTaukei भूमि हेतु TLTB सहमति, या राज्य भूमि हेतु भूमि निदेशक की स्वीकृति।" },
        { t: "पट्टे पर बातचीत करें", d: "अवधि, प्रीमियम और वार्षिक किराया तय करें; वाणिज्यिक पट्टे आमतौर पर 30–99 वर्ष के होते हैं।" },
        { t: "पट्टा पंजीकृत करें", d: "टाइटल रजिस्ट्रार के पास पंजीकरण कराएँ ताकि आपका हित कानूनी रूप से सुरक्षित रहे।" },
      ],
    },
    sectors: {
      overline: "क्षेत्रीय अवसर व सफलता की कहानियाँ",
      title: "असली सेटअप, असली क्षेत्र।",
      sub: "उन निवेशकों की झलक जिन्हें हमने पहली पूछताछ से लेकर फ़िजी में संचालित कंपनी तक मार्गदर्शन दिया।",
      note: "सामान्य ग्राहक परिणामों का उदाहरणात्मक चित्रण।",
      stories: [
        { sector: "पर्यटन", quote: "हमारे पहुँचने से पहले उन्होंने हर परमिट का खाका तैयार कर दिया था। हमारी रिज़ॉर्ट कंपनी कुछ ही हफ़्तों में पंजीकृत और बैंकिंग-तैयार थी।", name: "बुटीक रिज़ॉर्ट निवेशक", detail: "रिमोट पंजीकरण · एक यात्रा में बैंकिंग पूर्ण" },
        { sector: "कृषि-व्यवसाय", quote: "अकेले भूमि-पट्टा मार्गदर्शन ने हमारे महीने बचा दिए। हमें ठीक-ठीक पता था कि कौन-सी सहमतियाँ चाहिए।", name: "निर्यात कृषि-व्यवसाय संस्थापक", detail: "TLTB पट्टा प्राप्त · निर्यात लाइसेंस पूर्ण" },
        { sector: "प्रौद्योगिकी व BPO", quote: "पहली बार विदेशी संस्थापक के रूप में मुझे अफ़रा-तफ़री की आशंका थी। इसके बजाय एक टीम, एक स्पष्ट राह मिली।", name: "BPO सेवा निदेशक", detail: "8-चरण सेटअप · 12 कर्मचारियों के कार्य परमिट" },
      ],
    },
    updates: {
      overline: "अपडेट",
      title: "हाल के नीति व नियामक नोट।",
      sub: "जानने योग्य बदलावों की एक छोटी, सरल-भाषा फ़ीड।",
      note: "सामान्य जानकारी, सलाह नहीं। कार्रवाई से पहले हमसे वर्तमान आवश्यकताओं की पुष्टि करें।",
      items: [
        { date: "06 / 2026", tag: "कर", text: "FRCS VAT के लिए चरणबद्ध डिजिटल फाइलिंग जारी रखे हुए है — अधिकांश फाइलिंग अब ऑनलाइन है।" },
        { date: "05 / 2026", tag: "निवेश", text: "Investment Fiji ने पर्यटन, नवीकरणीय ऊर्जा और कृषि-व्यवसाय में क्षेत्रीय प्राथमिकताएँ दोहराईं।" },
        { date: "04 / 2026", tag: "आप्रवासन", text: "पूर्ण आवेदनों पर निवेशक परमिट की प्रक्रिया लगभग 21 कार्यदिवस पर स्थिर है।" },
        { date: "03 / 2026", tag: "बैंकिंग", text: "कॉर्पोरेट खाता खोलने हेतु अब भी एक बार व्यक्तिगत उपस्थिति आवश्यक; दस्तावेज़ चेकलिस्ट अद्यतन।" },
      ],
    },
    privacy: {
      title: "गोपनीयता नीति",
      updated: "जून 2026",
      clauses: [
        { h: "हम कौन हैं", body: ["Alvin Kumar & Associates (trading as AK Fiji) एक पंजीकृत सलाहकार फर्म है, जिसके कार्यालय फ़िजी के नाडी और सुवा में हैं। यह नीति बताती है कि इस वेबसाइट के माध्यम से आपके द्वारा साझा की गई व्यक्तिगत जानकारी को हम कैसे एकत्र, उपयोग और सुरक्षित करते हैं।"] },
        { h: "हम कौन-सी जानकारी एकत्र करते हैं", body: ["जब आप हमारी मुफ़्त गाइड का अनुरोध करते हैं तो हम आपके द्वारा दी गई जानकारी एकत्र करते हैं: पूरा नाम, ईमेल पता, फ़ोन नंबर और (वैकल्पिक रूप से) आपका स्थान। हम यह भी दर्ज करते हैं कि आपने हमारी संचार प्राप्त करने की सहमति दी या नहीं।"] },
        { h: "हम आपकी जानकारी का उपयोग कैसे करते हैं", body: ["हम आपकी जानकारी का उपयोग अनुरोधित गाइड व संबंधित सामग्री भेजने, फ़िजी में व्यवसाय स्थापित करने संबंधी आपकी पूछताछ का उत्तर देने, और अनुरोध पर कोटेशन व परामर्श निर्धारण प्रदान करने के लिए करते हैं। हम आपकी व्यक्तिगत जानकारी तीसरे पक्ष को नहीं बेचते।"] },
        { h: "डेटा प्रतिधारण व सुरक्षा", body: ["आपकी जानकारी सुरक्षित रूप से संग्रहीत की जाती है और केवल तब तक रखी जाती है जब तक सेवाएँ प्रदान करने और लागू रिकॉर्ड-कीपिंग दायित्वों के पालन के लिए आवश्यक हो। पहुँच केवल अधिकृत टीम सदस्यों तक सीमित है।"] },
        { h: "आपके अधिकार", body: ["आप अपने व्यक्तिगत डेटा तक पहुँच, उसमें सुधार या उसे हटाने का अनुरोध कर सकते हैं, और हमसे संपर्क करके किसी भी समय हमारी संचार से बाहर हो सकते हैं।"] },
        { h: "कुकीज़", body: ["हम इस साइट को चलाने और कुल ट्रैफ़िक समझने के लिए आवश्यक कुकीज़ का उपयोग करते हैं। आप अपनी पहली विज़िट पर दिखाए गए बैनर के माध्यम से गैर-आवश्यक कुकीज़ स्वीकार या अस्वीकार कर सकते हैं।"] },
      ],
      contactHeading: "संपर्क",
      contactLead: "इस नीति के बारे में प्रश्न? हमसे संपर्क करें:",
    },
    terms: {
      title: "सेवा की शर्तें",
      updated: "जून 2026",
      clauses: [
        { h: "शर्तों की स्वीकृति", body: ["इस वेबसाइट का उपयोग करके और हमारी मुफ़्त गाइड का अनुरोध करके, आप इन शर्तों से सहमत होते हैं। यदि आप सहमत नहीं हैं, तो कृपया साइट का उपयोग न करें।"] },
        { h: "जानकारी की प्रकृति", body: ["इस साइट पर दी गई गाइड, चेकलिस्ट, समय-सीमाएँ, फीस और कर आंकड़े केवल सामान्य जानकारी हैं। ये कानूनी, कर या वित्तीय सलाह नहीं हैं; दरें व आवश्यकताएँ फ़िजी राजस्व एवं सीमा शुल्क सेवा (FRCS) व अन्य प्राधिकरणों के नवीनतम मार्गदर्शन के अधीन हैं। सटीक दायित्व जुड़ाव के समय पुष्ट होते हैं।"] },
        { h: "परिणाम की कोई गारंटी नहीं", body: ["यद्यपि हम फ़िजी में व्यवसाय स्थापित करने के वास्तविक अनुभव पर आधारित हैं, समय-सीमाएँ व अनुमोदन तीसरे पक्ष के प्राधिकरणों और आपकी विशिष्ट परिस्थितियों पर निर्भर करते हैं। हम किसी विशेष परिणाम या प्रक्रिया समय की गारंटी नहीं देते।"] },
        { h: "आपकी ज़िम्मेदारियाँ", body: ["आप सटीक जानकारी देने और इन सामग्रियों का उपयोग अपने वैध व्यावसायिक नियोजन के लिए करने पर सहमत होते हैं। निर्णय लेने से पहले वर्तमान आवश्यकताओं की पुष्टि करना आपकी ज़िम्मेदारी है।"] },
        { h: "देयता की सीमा", body: ["कानून द्वारा अनुमत अधिकतम सीमा तक, इस वेबसाइट पर दी गई सामान्य जानकारी पर निर्भरता से उत्पन्न किसी भी हानि के लिए Alvin Kumar & Associates (trading as AK Fiji) उत्तरदायी नहीं है।"] },
      ],
      contactHeading: "संपर्क",
      contactLead: "इन शर्तों के बारे में प्रश्न? हमसे संपर्क करें:",
    },
  },

  ja: {
    legalCommon: { legal: "法務", back: "戻る" },
    legal: {
      overline: "法務・規制の要点",
      title: "ルールを、平易な言葉で。",
      reviewed: "2026年6月レビュー済み",
      items: [
        { k: "法人所得税", v: "25%" },
        { k: "VAT", v: "12.5%" },
        { k: "源泉徴収税", v: "契約時に確定 — 最新のFRCSガイダンスに従います。" },
        { k: "規制業種・所有比率", v: "業種ごとの所有比率上限と手続きは異なります。" },
        { k: "送金・外国為替管理", v: "フィジー準備銀行（RBF）の監督下で管理されます。" },
      ],
      disclaimer: "税額は一般的な目安であり、フィジー歳入関税局（FRCS）の最新ガイダンスに従います。正確な税率と義務は契約時に確定します。",
    },
    incentives: {
      overline: "投資インセンティブ",
      title: "知っておきたい業種別インセンティブ。",
      reviewed: "インセンティブは契約時に確認。概要は2026年6月に最終レビュー",
      items: [
        { t: "観光・ホスピタリティ", d: "関税減免、加速償却、業種別の助成金。" },
        { t: "クリーンエネルギー・農業関連", d: "税の免除期間、土地リースの支援、グリーンファイナンス連携。" },
        { t: "テクノロジー・BPO", d: "スキル開発補助と経済特区の優遇。" },
      ],
    },
    land: {
      overline: "土地・リース",
      title: "フィジーの土地所有とリースの仕組み。",
      sub: "フィジーの土地制度を理解すれば、外国人投資家も安心して事業を行えます。要点とリース取得の手順をご紹介します。",
      typesHeading: "土地の種類と所有比率の上限",
      types: [
        { t: "iTaukei（先住民）土地", d: "フィジーの土地の約90%。売却できず、リースのみ可能で、通常はiTaukei土地信託委員会（TLTB）を通じて行います。" },
        { t: "国有地", d: "政府所有で、土地省を通じてリースされます。商業・工業用地で一般的です。" },
        { t: "フリーホールド土地", d: "売買可能なわずかな割合（約8%）。外国人の購入者は町境付近の住宅フリーホールドに制限があります。" },
        { t: "外国人所有の上限", d: "外国人は原則として自治体境界内の住宅用地を所有できません。商業・投資リースは承認を得れば可能です。" },
      ],
      stepsHeading: "リース取得 — 4つのステップ",
      steps: [
        { t: "土地の種類を特定", d: "iTaukei・国有・フリーホールドのいずれかを確認。これで承認ルート全体が決まります。" },
        { t: "同意を取得", d: "iTaukei土地はTLTBの同意、国有地は土地局長の承認が必要です。" },
        { t: "リース条件を交渉", d: "期間、プレミアム、年間賃料を合意。商業リースは通常30〜99年です。" },
        { t: "リースを登記", d: "権利登記官に登記し、あなたの権利を法的に保護します。" },
      ],
    },
    sectors: {
      overline: "業種の機会と成功事例",
      title: "実際の設立、実際の業種。",
      sub: "最初のお問い合わせからフィジーでの事業会社設立まで、当社が支援した投資家のスナップショット。",
      note: "一般的な顧客成果を示す例示です。",
      stories: [
        { sector: "観光", quote: "渡航前にすべての許可を整理してくれました。リゾート会社は数週間で登記され、口座開設の準備も整いました。", name: "ブティックリゾート投資家", detail: "リモート登記 · 1回の渡航で口座開設完了" },
        { sector: "農業関連", quote: "土地リースの助言だけで数か月を節約できました。必要な同意が明確でした。", name: "輸出農業関連の創業者", detail: "TLTBリース取得 · 輸出ライセンス完了" },
        { sector: "テクノロジー・BPO", quote: "初めての外国人創業者として混乱を覚悟していました。実際は一つのチーム、明快な道筋でした。", name: "BPOサービス責任者", detail: "8ステップ設立 · 12名分の就労許可" },
      ],
    },
    updates: {
      overline: "最新情報",
      title: "最近の政策・規制メモ。",
      sub: "知っておく価値のある変化を、平易な言葉で短くお届けします。",
      note: "一般情報であり助言ではありません。行動の前に現行要件を当社にご確認ください。",
      items: [
        { date: "06 / 2026", tag: "税務", text: "FRCSはVATの段階的な電子申告を継続 — 多くの申告がオンライン化されています。" },
        { date: "05 / 2026", tag: "投資", text: "Investment Fijiが観光・再生可能エネルギー・農業関連の重点分野を再確認しました。" },
        { date: "04 / 2026", tag: "移民", text: "書類が揃った申請の投資家許可は約21営業日で推移しています。" },
        { date: "03 / 2026", tag: "銀行", text: "法人口座開設には依然として1回の来訪が必要。書類チェックリストを更新しました。" },
      ],
    },
    privacy: {
      title: "プライバシーポリシー",
      updated: "2026年6月",
      clauses: [
        { h: "当社について", body: ["Alvin Kumar & Associates (trading as AK Fiji)は、フィジーのナンディとスバにオフィスを構える登録済みアドバイザリー会社です。本ポリシーは、本ウェブサイトを通じてお客様が共有される個人情報を、当社がどのように収集・利用・保護するかを説明します。"] },
        { h: "収集する情報", body: ["無料ガイドをご請求いただく際、ご入力いただく情報を収集します：氏名、メールアドレス、電話番号、および（任意で）所在地。当社からの連絡受信への同意の有無も記録します。"] },
        { h: "情報の利用方法", body: ["ご請求のガイドや関連資料の送付、フィジーでの事業設立に関するお問い合わせへの回答、ご要望に応じた見積りや相談予約の提供のために情報を利用します。個人情報を第三者に販売することはありません。"] },
        { h: "データの保持とセキュリティ", body: ["お客様の情報は安全に保管し、サービス提供および適用される記録保持義務の遵守に必要な期間のみ保持します。アクセスは権限のあるチームメンバーに限定されます。"] },
        { h: "お客様の権利", body: ["個人データへのアクセス、訂正、削除を請求でき、当社に連絡することでいつでも連絡の受信を停止できます。"] },
        { h: "クッキー", body: ["本サイトの動作と全体的なアクセス状況の把握のため必須クッキーを使用します。初回訪問時に表示されるバナーで、非必須クッキーを承諾または拒否できます。"] },
      ],
      contactHeading: "お問い合わせ",
      contactLead: "本ポリシーに関するご質問は、こちらまで：",
    },
    terms: {
      title: "利用規約",
      updated: "2026年6月",
      clauses: [
        { h: "規約への同意", body: ["本ウェブサイトを利用し、無料ガイドをご請求いただくことで、本規約に同意したものとみなされます。同意されない場合は、本サイトをご利用にならないでください。"] },
        { h: "情報の性質", body: ["本サイトで提供するガイド、チェックリスト、所要期間、費用、税額は一般情報のみです。法務・税務・財務上の助言ではなく、税率と要件はフィジー歳入関税局（FRCS）その他当局の最新ガイダンスに従います。正確な義務は契約時に確定します。"] },
        { h: "結果の非保証", body: ["当社はフィジーでの事業設立の実績に基づきますが、所要期間や承認は第三者当局とお客様固有の状況に左右されます。特定の結果や処理時間を保証するものではありません。"] },
        { h: "お客様の責任", body: ["正確な情報を提供し、資料をご自身の適法な事業計画のために利用することに同意いただきます。意思決定の前に現行要件を確認する責任はお客様にあります。"] },
        { h: "責任の制限", body: ["法律が認める最大限の範囲で、Alvin Kumar & Associates (trading as AK Fiji)は本ウェブサイトの一般情報への依拠から生じるいかなる損失についても責任を負いません。"] },
      ],
      contactHeading: "お問い合わせ",
      contactLead: "本規約に関するご質問は、こちらまで：",
    },
  },

  ko: {
    legalCommon: { legal: "법률", back: "뒤로" },
    legal: {
      overline: "법률 및 규제 핵심",
      title: "규칙을 쉬운 말로.",
      reviewed: "2026년 6월 검토",
      items: [
        { k: "법인소득세", v: "25%" },
        { k: "VAT", v: "12.5%" },
        { k: "원천징수세", v: "계약 시 확정 — 최신 FRCS 지침에 따름." },
        { k: "규제 업종 및 지분", v: "업종별 지분 상한과 준수 절차가 다릅니다." },
        { k: "송금 및 외환 관리", v: "피지 중앙은행(RBF) 감독하에 관리됩니다." },
      ],
      disclaimer: "세금 수치는 일반 안내이며 피지 국세관세청(FRCS)의 최신 지침에 따릅니다. 정확한 세율과 의무는 계약 시 확정됩니다.",
    },
    incentives: {
      overline: "투자 인센티브",
      title: "알아둘 만한 업종별 인센티브.",
      reviewed: "인센티브는 계약 시 확인; 개요 최종 검토 2026년 6월",
      items: [
        { t: "관광 및 접객", d: "관세 감면, 가속 상각, 업종별 보조금." },
        { t: "청정에너지 및 농산업", d: "면세 기간, 토지 임대 지원, 그린 파이낸스 파트너십." },
        { t: "기술 및 BPO", d: "기술 개발 보조금과 경제특구 혜택." },
      ],
    },
    land: {
      overline: "토지 및 임대",
      title: "피지의 토지 소유와 임대 방식.",
      sub: "피지의 토지 제도를 이해하면 외국인 투자자도 자신 있게 사업을 운영할 수 있습니다. 핵심 개요와 임대 확보 절차를 소개합니다.",
      typesHeading: "토지 유형 및 소유 상한",
      types: [
        { t: "iTaukei(원주민) 토지", d: "피지 토지의 약 90%. 매각할 수 없으며 임대만 가능하고, 보통 iTaukei 토지신탁위원회(TLTB)를 통해 진행합니다." },
        { t: "국유지", d: "정부 소유로 토지부를 통해 임대됩니다. 상업·산업 부지에 일반적입니다." },
        { t: "자유보유(프리홀드) 토지", d: "매매 가능한 소수 비중(약 8%). 외국인 매수자는 도심 경계 인근 주거용 프리홀드에 제한이 있습니다." },
        { t: "외국인 소유 상한", d: "외국인은 원칙적으로 자치단체 경계 내 주거용 토지를 소유할 수 없습니다. 상업·투자 임대는 승인 시 가능합니다." },
      ],
      stepsHeading: "임대 확보 — 네 단계",
      steps: [
        { t: "토지 유형 확인", d: "부지가 iTaukei, 국유지, 프리홀드 중 무엇인지 확인 — 전체 승인 경로가 여기서 결정됩니다." },
        { t: "동의 확보", d: "iTaukei 토지는 TLTB 동의, 국유지는 토지부장 승인이 필요합니다." },
        { t: "임대 협상", d: "기간, 프리미엄, 연 임대료를 합의합니다. 상업 임대는 보통 30~99년입니다." },
        { t: "임대 등기", d: "권리등기소에 등기하여 귀하의 권리를 법적으로 보호합니다." },
      ],
    },
    sectors: {
      overline: "업종 기회 및 성공 사례",
      title: "실제 설립, 실제 업종.",
      sub: "첫 문의부터 피지 내 운영 회사까지, 저희가 안내한 투자자들의 사례입니다.",
      note: "일반적인 고객 성과를 보여주는 예시입니다.",
      stories: [
        { sector: "관광", quote: "저희가 입국하기 전에 모든 허가를 정리해 주었습니다. 리조트 회사는 몇 주 만에 등록되고 은행 개설 준비까지 마쳤습니다.", name: "부티크 리조트 투자자", detail: "원격 등록 · 한 번의 방문으로 은행 개설 완료" },
        { sector: "농산업", quote: "토지 임대 안내만으로도 몇 달을 아꼈습니다. 어떤 동의가 필요한지 정확히 알았죠.", name: "수출 농산업 창업자", detail: "TLTB 임대 확보 · 수출 라이선스 완료" },
        { sector: "기술 및 BPO", quote: "첫 외국인 창업자로서 혼란을 예상했지만, 실제로는 한 팀, 하나의 명확한 경로였습니다.", name: "BPO 서비스 이사", detail: "8단계 설립 · 직원 12명 취업 허가" },
      ],
    },
    updates: {
      overline: "업데이트",
      title: "최근 정책 및 규제 노트.",
      sub: "알아둘 만한 변화를 쉬운 말로 짧게 전합니다.",
      note: "일반 정보이며 자문이 아닙니다. 실행 전 현행 요건을 저희와 확인하세요.",
      items: [
        { date: "06 / 2026", tag: "세무", text: "FRCS가 VAT 단계적 전자 신고를 계속 — 대부분의 신고가 이제 온라인입니다." },
        { date: "05 / 2026", tag: "투자", text: "Investment Fiji가 관광·재생에너지·농산업의 업종 우선순위를 재확인했습니다." },
        { date: "04 / 2026", tag: "이민", text: "서류가 완비된 신청의 투자자 허가 처리 기간은 약 21영업일을 유지하고 있습니다." },
        { date: "03 / 2026", tag: "은행", text: "법인 계좌 개설에는 여전히 1회 대면 방문이 필요하며, 서류 체크리스트가 갱신되었습니다." },
      ],
    },
    privacy: {
      title: "개인정보처리방침",
      updated: "2026년 6월",
      clauses: [
        { h: "회사 소개", body: ["Alvin Kumar & Associates (trading as AK Fiji)는 피지 난디와 수바에 사무소를 둔 등록 자문 회사입니다. 본 방침은 이 웹사이트를 통해 귀하가 공유한 개인정보를 저희가 어떻게 수집·이용·보호하는지 설명합니다."] },
        { h: "수집하는 정보", body: ["무료 가이드를 요청하시면 입력하신 정보를 수집합니다: 성명, 이메일 주소, 전화번호, 그리고 선택적으로 위치. 또한 저희 커뮤니케이션 수신 동의 여부를 기록합니다."] },
        { h: "정보 이용 방법", body: ["요청하신 가이드 및 관련 자료 발송, 피지 사업 설립 관련 문의 응대, 요청 시 견적 및 상담 예약 제공을 위해 정보를 이용합니다. 개인정보를 제3자에게 판매하지 않습니다."] },
        { h: "데이터 보관 및 보안", body: ["귀하의 정보는 안전하게 보관되며 서비스 제공과 관련 기록 보존 의무 준수에 필요한 기간 동안만 보관됩니다. 접근은 권한이 있는 팀원으로 제한됩니다."] },
        { h: "귀하의 권리", body: ["개인정보에 대한 열람·정정·삭제를 요청할 수 있으며, 저희에게 연락하여 언제든지 커뮤니케이션 수신을 거부할 수 있습니다."] },
        { h: "쿠키", body: ["사이트 작동과 전체 트래픽 파악을 위해 필수 쿠키를 사용합니다. 첫 방문 시 표시되는 배너를 통해 비필수 쿠키를 수락하거나 거부할 수 있습니다."] },
      ],
      contactHeading: "문의",
      contactLead: "본 방침에 대한 문의는 아래로 연락 주세요:",
    },
    terms: {
      title: "서비스 약관",
      updated: "2026년 6월",
      clauses: [
        { h: "약관 동의", body: ["이 웹사이트를 이용하고 무료 가이드를 요청함으로써 본 약관에 동의하게 됩니다. 동의하지 않으시면 사이트를 이용하지 마십시오."] },
        { h: "정보의 성격", body: ["본 사이트에서 제공하는 가이드, 체크리스트, 일정, 수수료 및 세금 수치는 일반 정보일 뿐입니다. 법률·세무·재무 자문이 아니며, 세율과 요건은 피지 국세관세청(FRCS) 및 기타 당국의 최신 지침에 따릅니다. 정확한 의무는 계약 시 확정됩니다."] },
        { h: "결과 미보장", body: ["저희는 피지에서의 실제 사업 설립 경험에 기반하지만, 일정과 승인은 제3자 당국과 귀하의 구체적 상황에 따라 달라집니다. 특정 결과나 처리 시간을 보장하지 않습니다."] },
        { h: "귀하의 책임", body: ["정확한 정보를 제공하고 자료를 귀하의 적법한 사업 계획에 이용하는 데 동의합니다. 결정 전에 현행 요건을 확인할 책임은 귀하에게 있습니다."] },
        { h: "책임의 제한", body: ["법이 허용하는 최대 범위에서, Alvin Kumar & Associates (trading as AK Fiji)는 본 웹사이트의 일반 정보에 대한 의존으로 발생하는 어떠한 손실에도 책임을 지지 않습니다."] },
      ],
      contactHeading: "문의",
      contactLead: "본 약관에 대한 문의는 아래로 연락 주세요:",
    },
  },

  es: {
    legalCommon: { legal: "Legal", back: "Volver" },
    legal: {
      overline: "Aspectos legales y regulatorios",
      title: "Las reglas, en términos claros.",
      reviewed: "Revisado en junio de 2026",
      items: [
        { k: "Impuesto de sociedades", v: "25%" },
        { k: "VAT", v: "12,5%" },
        { k: "Retención en la fuente", v: "Se confirma al contratar — sujeto a la última guía de FRCS." },
        { k: "Sectores reservados y propiedad", v: "Los límites de propiedad y los pasos de cumplimiento varían según el sector." },
        { k: "Repatriación y control de cambios", v: "Gestionado bajo la supervisión del Banco de Reserva de Fiyi (RBF)." },
      ],
      disclaimer: "Las cifras fiscales son orientativas y están sujetas a la última guía del Servicio de Ingresos y Aduanas de Fiyi (FRCS). Las tasas y obligaciones exactas se confirman al contratar.",
    },
    incentives: {
      overline: "Incentivos a la inversión",
      title: "Incentivos sectoriales que conviene conocer.",
      reviewed: "Incentivos verificados al contratar; resumen revisado por última vez en junio de 2026",
      items: [
        { t: "Turismo y hostelería", d: "Concesiones arancelarias, amortización acelerada y ayudas específicas del sector." },
        { t: "Energía limpia y agroindustria", d: "Exenciones fiscales, facilitación de arrendamiento de tierras y alianzas de financiación verde." },
        { t: "Tecnología y BPO", d: "Subvenciones para el desarrollo de competencias y beneficios de zonas económicas especiales." },
      ],
    },
    land: {
      overline: "Tierra y arrendamiento",
      title: "Cómo funcionan la propiedad y el arrendamiento de tierras en Fiyi.",
      sub: "Los inversores extranjeros operan con confianza al entender el sistema de tierras de Fiyi. Aquí tiene el panorama esencial y los pasos para asegurar un arrendamiento.",
      typesHeading: "Tipos de tierra y límites de propiedad",
      types: [
        { t: "Tierra iTaukei (nativa)", d: "Cerca del 90% de la tierra de Fiyi. No se puede vender — solo arrendar, normalmente a través del iTaukei Land Trust Board (TLTB)." },
        { t: "Tierra estatal", d: "Tierra de propiedad estatal arrendada a través del Departamento de Tierras. Común para sitios comerciales e industriales." },
        { t: "Tierra en propiedad plena (freehold)", d: "Una pequeña parte (~8%) que se puede comprar y vender. Los compradores extranjeros tienen límites en el freehold residencial cerca de los límites urbanos." },
        { t: "Límites de propiedad extranjera", d: "Por lo general, los extranjeros no pueden poseer tierra residencial dentro de los límites municipales; los arrendamientos comerciales y de inversión siguen abiertos con aprobación." },
      ],
      stepsHeading: "Asegurar un arrendamiento — cuatro pasos",
      steps: [
        { t: "Identifique el tipo de tierra", d: "Confirme si el sitio es iTaukei, estatal o freehold — esto define toda la ruta de aprobación." },
        { t: "Obtenga el consentimiento", d: "Consentimiento del TLTB para tierra iTaukei, o aprobación del Director de Tierras para tierra estatal." },
        { t: "Negocie el arrendamiento", d: "Acuerde plazo, prima y renta anual; los arrendamientos comerciales suelen ser de 30 a 99 años." },
        { t: "Registre el arrendamiento", d: "Regístrelo en el Registro de Títulos para que su interés quede protegido legalmente." },
      ],
    },
    sectors: {
      overline: "Oportunidades sectoriales e historias de éxito",
      title: "Constituciones reales, sectores reales.",
      sub: "Una muestra de inversores a los que hemos guiado desde la primera consulta hasta una empresa operativa en Fiyi.",
      note: "Ilustrativo de resultados típicos de clientes.",
      stories: [
        { sector: "Turismo", quote: "Trazaron cada permiso antes de que voláramos. Nuestra empresa de resort quedó registrada y lista para la banca en semanas.", name: "Inversor de resort boutique", detail: "Registrado en remoto · banca cerrada en un solo viaje" },
        { sector: "Agroindustria", quote: "Solo la orientación sobre el arrendamiento de tierras nos ahorró meses. Sabíamos exactamente qué consentimientos necesitábamos.", name: "Fundador de agroindustria de exportación", detail: "Arrendamiento TLTB asegurado · licencias de exportación completas" },
        { sector: "Tecnología y BPO", quote: "Como fundador extranjero primerizo esperaba caos. En cambio fue un solo equipo, un camino claro.", name: "Director de servicios BPO", detail: "Constitución de 8 pasos · permisos de trabajo para 12 empleados" },
      ],
    },
    updates: {
      overline: "Novedades",
      title: "Notas recientes de política y regulación.",
      sub: "Una breve lista, en lenguaje claro, de cambios que conviene conocer.",
      note: "Información general, no asesoramiento. Confirme los requisitos vigentes con nosotros antes de actuar.",
      items: [
        { date: "06 / 2026", tag: "Impuestos", text: "FRCS continúa la presentación digital por fases del VAT — la mayoría de las declaraciones ya son en línea." },
        { date: "05 / 2026", tag: "Inversión", text: "Investment Fiji reafirmó las prioridades sectoriales en turismo, energía renovable y agroindustria." },
        { date: "04 / 2026", tag: "Inmigración", text: "El trámite del permiso de inversor se mantiene en unos 21 días hábiles en solicitudes completas." },
        { date: "03 / 2026", tag: "Banca", text: "La apertura de cuenta corporativa aún requiere una visita presencial; se actualizaron las listas de documentos." },
      ],
    },
    privacy: {
      title: "Política de privacidad",
      updated: "junio de 2026",
      clauses: [
        { h: "Quiénes somos", body: ["Alvin Kumar & Associates (trading as AK Fiji) es una firma asesora registrada con oficinas en Nadi y Suva, Fiyi. Esta política explica cómo recopilamos, usamos y protegemos la información personal que comparte con nosotros a través de este sitio web."] },
        { h: "Información que recopilamos", body: ["Cuando solicita nuestra guía gratuita, recopilamos los datos que envía: nombre completo, correo electrónico, número de teléfono y, opcionalmente, su ubicación. También registramos si consintió recibir nuestras comunicaciones."] },
        { h: "Cómo usamos su información", body: ["Usamos su información para enviarle la guía solicitada y materiales de seguimiento, responder a sus consultas sobre la constitución de una empresa en Fiyi y ofrecer presupuestos y agenda de consultas cuando los solicite. No vendemos su información personal a terceros."] },
        { h: "Conservación y seguridad de los datos", body: ["Sus datos se almacenan de forma segura y se conservan solo el tiempo necesario para prestar nuestros servicios y cumplir las obligaciones aplicables de conservación de registros. El acceso se limita a miembros autorizados del equipo."] },
        { h: "Sus derechos", body: ["Puede solicitar el acceso, la corrección o la eliminación de sus datos personales, y puede darse de baja de nuestras comunicaciones en cualquier momento contactándonos."] },
        { h: "Cookies", body: ["Usamos cookies esenciales para que este sitio funcione y para entender el tráfico agregado. Puede aceptar o rechazar las cookies no esenciales mediante el banner que se muestra en su primera visita."] },
      ],
      contactHeading: "Contacto",
      contactLead: "¿Preguntas sobre esta política? Contáctenos en:",
    },
    terms: {
      title: "Términos del servicio",
      updated: "junio de 2026",
      clauses: [
        { h: "Aceptación de los términos", body: ["Al usar este sitio web y solicitar nuestra guía gratuita, acepta estos términos. Si no está de acuerdo, no use el sitio."] },
        { h: "Naturaleza de la información", body: ["La guía, listas, plazos, tarifas y cifras fiscales de este sitio son solo información general. No constituyen asesoramiento legal, fiscal ni financiero, y las tasas y requisitos están sujetos a la última guía del Servicio de Ingresos y Aduanas de Fiyi (FRCS) y otras autoridades. Las obligaciones exactas se confirman al contratar."] },
        { h: "Sin garantía de resultado", body: ["Aunque nos basamos en experiencia real constituyendo empresas en Fiyi, los plazos y aprobaciones dependen de autoridades externas y de sus circunstancias específicas. No garantizamos ningún resultado ni tiempo de tramitación concretos."] },
        { h: "Sus responsabilidades", body: ["Se compromete a facilitar información precisa y a usar los materiales para su propia planificación empresarial lícita. Es responsable de verificar los requisitos vigentes antes de tomar decisiones."] },
        { h: "Limitación de responsabilidad", body: ["En la máxima medida permitida por la ley, Alvin Kumar & Associates (trading as AK Fiji) no es responsable de ninguna pérdida derivada de confiar en la información general de este sitio web."] },
      ],
      contactHeading: "Contacto",
      contactLead: "¿Preguntas sobre estos términos? Contáctenos en:",
    },
  },

  fr: {
    legalCommon: { legal: "Mentions légales", back: "Retour" },
    legal: {
      overline: "Essentiels juridiques et réglementaires",
      title: "Les règles, en termes clairs.",
      reviewed: "Revu en juin 2026",
      items: [
        { k: "Impôt sur les sociétés", v: "25%" },
        { k: "VAT", v: "12,5%" },
        { k: "Retenue à la source", v: "Confirmée à l'engagement — soumise aux dernières directives de la FRCS." },
        { k: "Secteurs réservés et propriété", v: "Les plafonds de participation et les étapes de conformité varient selon le secteur." },
        { k: "Rapatriement et contrôle des changes", v: "Géré sous la supervision de la Banque de réserve des Fidji (RBF)." },
      ],
      disclaimer: "Les chiffres fiscaux sont indicatifs et soumis aux dernières directives du Service des impôts et douanes des Fidji (FRCS). Les taux et obligations exacts sont confirmés à l'engagement.",
    },
    incentives: {
      overline: "Incitations à l'investissement",
      title: "Des incitations sectorielles à connaître.",
      reviewed: "Incitations vérifiées à l'engagement ; aperçu revu pour la dernière fois en juin 2026",
      items: [
        { t: "Tourisme et hôtellerie", d: "Concessions de droits, amortissement accéléré et subventions sectorielles." },
        { t: "Énergie propre et agro-industrie", d: "Exonérations fiscales, facilitation des baux fonciers et partenariats de finance verte." },
        { t: "Technologie et BPO", d: "Subventions au développement des compétences et avantages des zones économiques spéciales." },
      ],
    },
    land: {
      overline: "Terrain et bail",
      title: "Comment fonctionnent la propriété et la location foncières aux Fidji.",
      sub: "Les investisseurs étrangers opèrent en confiance dès qu'ils comprennent le système foncier fidjien. Voici l'essentiel — et les étapes pour obtenir un bail.",
      typesHeading: "Types de terrain et plafonds de propriété",
      types: [
        { t: "Terre iTaukei (autochtone)", d: "Environ 90% des terres des Fidji. Elle ne peut être vendue — seulement louée, généralement via l'iTaukei Land Trust Board (TLTB)." },
        { t: "Terre domaniale", d: "Terre appartenant à l'État, louée via le Département des terres. Courante pour les sites commerciaux et industriels." },
        { t: "Terre en pleine propriété (freehold)", d: "Une petite part (~8%) qui peut être achetée et vendue. Les acheteurs étrangers ont des limites sur le freehold résidentiel près des limites urbaines." },
        { t: "Plafonds de propriété étrangère", d: "Les étrangers ne peuvent généralement pas posséder de terrain résidentiel dans les limites municipales ; les baux commerciaux et d'investissement restent ouverts avec approbation." },
      ],
      stepsHeading: "Obtenir un bail — quatre étapes",
      steps: [
        { t: "Identifier le type de terrain", d: "Confirmez si le site est iTaukei, domanial ou freehold — cela définit tout le parcours d'approbation." },
        { t: "Obtenir le consentement", d: "Consentement du TLTB pour la terre iTaukei, ou approbation du Directeur des terres pour la terre domaniale." },
        { t: "Négocier le bail", d: "Convenez de la durée, de la prime et du loyer annuel ; les baux commerciaux vont couramment de 30 à 99 ans." },
        { t: "Enregistrer le bail", d: "Enregistrez-le auprès du Conservateur des titres afin que votre droit soit juridiquement protégé." },
      ],
    },
    sectors: {
      overline: "Opportunités sectorielles et réussites",
      title: "Des créations réelles, des secteurs réels.",
      sub: "Un aperçu des investisseurs que nous avons accompagnés de la première demande à une société opérationnelle aux Fidji.",
      note: "Illustratif de résultats clients typiques.",
      stories: [
        { sector: "Tourisme", quote: "Ils avaient cartographié chaque permis avant notre arrivée. Notre société de resort était immatriculée et prête pour la banque en quelques semaines.", name: "Investisseur de resort boutique", detail: "Immatriculé à distance · banque finalisée en un seul voyage" },
        { sector: "Agro-industrie", quote: "Les seuls conseils sur le bail foncier nous ont fait gagner des mois. Nous savions exactement quels consentements il fallait.", name: "Fondateur d'agro-industrie d'export", detail: "Bail TLTB obtenu · licences d'exportation complètes" },
        { sector: "Technologie et BPO", quote: "En tant que fondateur étranger débutant, je m'attendais au chaos. Ce fut au contraire une seule équipe, un chemin clair.", name: "Directeur de services BPO", detail: "Création en 8 étapes · permis de travail pour 12 employés" },
      ],
    },
    updates: {
      overline: "Actualités",
      title: "Notes récentes de politique et de réglementation.",
      sub: "Un fil court, en langage clair, des changements à connaître.",
      note: "Information générale, pas un conseil. Confirmez les exigences en vigueur avec nous avant d'agir.",
      items: [
        { date: "06 / 2026", tag: "Fiscalité", text: "La FRCS poursuit la télédéclaration progressive de la VAT — la plupart des dépôts sont désormais en ligne." },
        { date: "05 / 2026", tag: "Investissement", text: "Investment Fiji a réaffirmé les priorités sectorielles : tourisme, énergies renouvelables et agro-industrie." },
        { date: "04 / 2026", tag: "Immigration", text: "Le traitement du permis d'investisseur se maintient autour de 21 jours ouvrés pour les dossiers complets." },
        { date: "03 / 2026", tag: "Banque", text: "L'ouverture de compte professionnel exige toujours une visite en personne ; listes de documents mises à jour." },
      ],
    },
    privacy: {
      title: "Politique de confidentialité",
      updated: "juin 2026",
      clauses: [
        { h: "Qui nous sommes", body: ["Alvin Kumar & Associates (trading as AK Fiji) est un cabinet de conseil enregistré, avec des bureaux à Nadi et Suva, aux Fidji. Cette politique explique comment nous collectons, utilisons et protégeons les informations personnelles que vous partagez via ce site."] },
        { h: "Informations que nous collectons", body: ["Lorsque vous demandez notre guide gratuit, nous collectons les informations que vous soumettez : nom complet, adresse e-mail, numéro de téléphone et, facultativement, votre localisation. Nous enregistrons aussi si vous avez consenti à recevoir nos communications."] },
        { h: "Comment nous utilisons vos informations", body: ["Nous utilisons vos informations pour vous envoyer le guide demandé et des documents de suivi, répondre à vos questions sur la création d'entreprise aux Fidji, et fournir des devis et la prise de rendez-vous lorsque vous le demandez. Nous ne vendons pas vos informations personnelles à des tiers."] },
        { h: "Conservation et sécurité des données", body: ["Vos informations sont stockées en sécurité et conservées uniquement le temps nécessaire pour fournir nos services et respecter les obligations applicables de conservation. L'accès est limité aux membres autorisés de l'équipe."] },
        { h: "Vos droits", body: ["Vous pouvez demander l'accès, la rectification ou la suppression de vos données personnelles, et vous désinscrire de nos communications à tout moment en nous contactant."] },
        { h: "Cookies", body: ["Nous utilisons des cookies essentiels pour faire fonctionner ce site et comprendre le trafic global. Vous pouvez accepter ou refuser les cookies non essentiels via la bannière affichée lors de votre première visite."] },
      ],
      contactHeading: "Contact",
      contactLead: "Des questions sur cette politique ? Contactez-nous :",
    },
    terms: {
      title: "Conditions d'utilisation",
      updated: "juin 2026",
      clauses: [
        { h: "Acceptation des conditions", body: ["En utilisant ce site et en demandant notre guide gratuit, vous acceptez ces conditions. Si vous n'êtes pas d'accord, veuillez ne pas utiliser le site."] },
        { h: "Nature des informations", body: ["Le guide, les listes, les délais, les frais et les chiffres fiscaux fournis sur ce site sont des informations générales uniquement. Ils ne constituent pas un conseil juridique, fiscal ou financier ; les taux et exigences sont soumis aux dernières directives du Service des impôts et douanes des Fidji (FRCS) et d'autres autorités. Les obligations exactes sont confirmées à l'engagement."] },
        { h: "Aucune garantie de résultat", body: ["Bien que nous nous appuyions sur une réelle expérience de création d'entreprises aux Fidji, les délais et approbations dépendent d'autorités tierces et de votre situation spécifique. Nous ne garantissons aucun résultat ni délai de traitement particulier."] },
        { h: "Vos responsabilités", body: ["Vous vous engagez à fournir des informations exactes et à utiliser les documents pour votre propre planification d'entreprise licite. Il vous incombe de vérifier les exigences en vigueur avant toute décision."] },
        { h: "Limitation de responsabilité", body: ["Dans toute la mesure permise par la loi, Alvin Kumar & Associates (trading as AK Fiji) n'est pas responsable des pertes découlant du recours aux informations générales fournies sur ce site."] },
      ],
      contactHeading: "Contact",
      contactLead: "Des questions sur ces conditions ? Contactez-nous :",
    },
  },

  ar: {
    legalCommon: { legal: "قانوني", back: "رجوع" },
    legal: {
      overline: "أساسيات قانونية وتنظيمية",
      title: "القواعد، بعبارات واضحة.",
      reviewed: "روجعت في يونيو 2026",
      items: [
        { k: "ضريبة دخل الشركات", v: "25%" },
        { k: "VAT", v: "12.5%" },
        { k: "ضريبة الاستقطاع", v: "تُؤكَّد عند التعاقد — وفقًا لأحدث إرشادات FRCS." },
        { k: "القطاعات المقيّدة والملكية", v: "تختلف حدود الملكية وخطوات الامتثال حسب القطاع." },
        { k: "التحويل والرقابة على النقد", v: "يُدار تحت إشراف مصرف فيجي الاحتياطي (RBF)." },
      ],
      disclaimer: "الأرقام الضريبية إرشادية عامة وتخضع لأحدث إرشادات دائرة الإيرادات والجمارك في فيجي (FRCS). تُؤكَّد المعدلات والالتزامات الدقيقة عند التعاقد.",
    },
    incentives: {
      overline: "حوافز الاستثمار",
      title: "حوافز قطاعية جديرة بالمعرفة.",
      reviewed: "تُتحقَّق الحوافز عند التعاقد؛ آخر مراجعة للنظرة العامة في يونيو 2026",
      items: [
        { t: "السياحة والضيافة", d: "تخفيضات جمركية، وإهلاك معجّل، ومنح خاصة بالقطاع." },
        { t: "الطاقة النظيفة والأعمال الزراعية", d: "إعفاءات ضريبية، وتيسير تأجير الأراضي، وشراكات التمويل الأخضر." },
        { t: "التكنولوجيا وBPO", d: "دعم تطوير المهارات ومزايا المناطق الاقتصادية الخاصة." },
      ],
    },
    land: {
      overline: "الأراضي والتأجير",
      title: "كيف تعمل ملكية الأراضي وتأجيرها في فيجي.",
      sub: "يعمل المستثمرون الأجانب بثقة بمجرد فهمهم لنظام الأراضي في فيجي. إليك الصورة الأساسية وخطوات تأمين عقد التأجير.",
      typesHeading: "أنواع الأراضي وحدود الملكية",
      types: [
        { t: "أراضي iTaukei (الأصلية)", d: "نحو 90% من أراضي فيجي. لا يمكن بيعها — تُؤجَّر فقط، عادةً عبر مجلس أمناء أراضي iTaukei (TLTB)." },
        { t: "أراضي الدولة", d: "أراضٍ مملوكة للحكومة تُؤجَّر عبر دائرة الأراضي. شائعة للمواقع التجارية والصناعية." },
        { t: "أراضي الملكية الحرة (Freehold)", d: "نسبة صغيرة (~8%) يمكن بيعها وشراؤها. يواجه المشترون الأجانب قيودًا على الملكية الحرة السكنية قرب حدود المدن." },
        { t: "حدود ملكية الأجانب", d: "لا يجوز للأجانب عمومًا امتلاك أراضٍ سكنية داخل الحدود البلدية؛ وتبقى عقود التأجير التجارية والاستثمارية متاحة بعد الموافقة." },
      ],
      stepsHeading: "تأمين عقد التأجير — أربع خطوات",
      steps: [
        { t: "حدّد نوع الأرض", d: "تأكّد إن كان الموقع iTaukei أو دولة أو ملكية حرة — فهذا يحدّد مسار الموافقة بأكمله." },
        { t: "احصل على الموافقة", d: "موافقة TLTB لأراضي iTaukei، أو موافقة مدير الأراضي لأراضي الدولة." },
        { t: "تفاوض على عقد التأجير", d: "اتفق على المدة والعلاوة والإيجار السنوي؛ عادةً ما تمتد عقود التأجير التجارية من 30 إلى 99 عامًا." },
        { t: "سجّل عقد التأجير", d: "سجّله لدى مسجّل السندات لتُحمى مصلحتك قانونيًا." },
      ],
    },
    sectors: {
      overline: "فرص القطاعات وقصص النجاح",
      title: "تأسيسات حقيقية، قطاعات حقيقية.",
      sub: "لمحة عن مستثمرين أرشدناهم من أول استفسار حتى شركة عاملة في فيجي.",
      note: "توضيحي لنتائج العملاء المعتادة.",
      stories: [
        { sector: "السياحة", quote: "رسموا كل تصريح قبل وصولنا. سُجِّلت شركة المنتجع وأصبحت جاهزة للخدمات البنكية خلال أسابيع.", name: "مستثمر منتجع بوتيك", detail: "تسجيل عن بُعد · إنهاء الخدمات البنكية في زيارة واحدة" },
        { sector: "الأعمال الزراعية", quote: "إرشاد تأجير الأرض وحده وفّر علينا أشهرًا. عرفنا تمامًا الموافقات التي نحتاجها.", name: "مؤسس أعمال زراعية تصديرية", detail: "تأمين عقد TLTB · إنهاء تراخيص التصدير" },
        { sector: "التكنولوجيا وBPO", quote: "كمؤسس أجنبي لأول مرة توقّعت الفوضى. لكن كان الأمر فريقًا واحدًا ومسارًا واضحًا.", name: "مدير خدمات BPO", detail: "تأسيس من 8 خطوات · تصاريح عمل لـ 12 موظفًا" },
      ],
    },
    updates: {
      overline: "المستجدات",
      title: "ملاحظات سياسية وتنظيمية حديثة.",
      sub: "موجز قصير بلغة واضحة لتغييرات تستحق المعرفة.",
      note: "معلومات عامة وليست نصيحة. أكّد المتطلبات الحالية معنا قبل التصرّف.",
      items: [
        { date: "06 / 2026", tag: "الضرائب", text: "تواصل FRCS التقديم الرقمي التدريجي لـ VAT — معظم الإقرارات أصبحت عبر الإنترنت." },
        { date: "05 / 2026", tag: "الاستثمار", text: "أكّدت Investment Fiji أولويات القطاعات في السياحة والطاقة المتجددة والأعمال الزراعية." },
        { date: "04 / 2026", tag: "الهجرة", text: "تبقى مدة معالجة تصريح المستثمر نحو 21 يوم عمل للطلبات المكتملة." },
        { date: "03 / 2026", tag: "البنوك", text: "ما زال فتح الحساب المؤسسي يتطلب زيارة حضورية واحدة؛ وقد حُدِّثت قوائم المستندات." },
      ],
    },
    privacy: {
      title: "سياسة الخصوصية",
      updated: "يونيو 2026",
      clauses: [
        { h: "من نحن", body: ["Alvin Kumar & Associates (trading as AK Fiji) شركة استشارية مسجّلة لها مكاتب في نادي وسوفا بفيجي. توضّح هذه السياسة كيف نجمع ونستخدم ونحمي المعلومات الشخصية التي تشاركها معنا عبر هذا الموقع."] },
        { h: "المعلومات التي نجمعها", body: ["عند طلب دليلنا المجاني نجمع البيانات التي ترسلها: الاسم الكامل والبريد الإلكتروني ورقم الهاتف، واختياريًا موقعك. كما نسجّل ما إذا وافقت على تلقّي مراسلاتنا."] },
        { h: "كيف نستخدم معلوماتك", body: ["نستخدم معلوماتك لإرسال الدليل المطلوب والمواد ذات الصلة، والرد على استفساراتك حول تأسيس عمل في فيجي، وتقديم عروض الأسعار وجدولة الاستشارات عند طلبها. لا نبيع معلوماتك الشخصية لأطراف ثالثة."] },
        { h: "الاحتفاظ بالبيانات وأمنها", body: ["تُخزَّن بياناتك بأمان وتُحفَظ فقط للمدة اللازمة لتقديم خدماتنا والامتثال لالتزامات حفظ السجلات المعمول بها. الوصول مقصور على أعضاء الفريق المصرّح لهم."] },
        { h: "حقوقك", body: ["يمكنك طلب الوصول إلى بياناتك الشخصية أو تصحيحها أو حذفها، ويمكنك إلغاء الاشتراك في مراسلاتنا في أي وقت بالتواصل معنا."] },
        { h: "ملفات تعريف الارتباط", body: ["نستخدم ملفات تعريف الارتباط الأساسية لتشغيل الموقع وفهم إجمالي الزيارات. يمكنك قبول أو رفض غير الأساسية منها عبر الشريط الذي يظهر عند أول زيارة."] },
      ],
      contactHeading: "التواصل",
      contactLead: "أسئلة حول هذه السياسة؟ تواصل معنا عبر:",
    },
    terms: {
      title: "شروط الخدمة",
      updated: "يونيو 2026",
      clauses: [
        { h: "قبول الشروط", body: ["باستخدام هذا الموقع وطلب دليلنا المجاني، فإنك توافق على هذه الشروط. إن لم توافق، فيرجى عدم استخدام الموقع."] },
        { h: "طبيعة المعلومات", body: ["الدليل والقوائم والمدد والرسوم والأرقام الضريبية المقدّمة في هذا الموقع هي معلومات عامة فقط. وليست نصيحة قانونية أو ضريبية أو مالية، وتخضع المعدلات والمتطلبات لأحدث إرشادات دائرة الإيرادات والجمارك في فيجي (FRCS) وغيرها من الجهات. تُؤكَّد الالتزامات الدقيقة عند التعاقد."] },
        { h: "لا ضمان للنتيجة", body: ["مع اعتمادنا على خبرة حقيقية في تأسيس الأعمال في فيجي، تعتمد المدد والموافقات على جهات خارجية وعلى ظروفك المحددة. لا نضمن أي نتيجة أو مدة معالجة بعينها."] },
        { h: "مسؤولياتك", body: ["توافق على تقديم معلومات دقيقة واستخدام المواد لتخطيط أعمالك المشروع. وتقع عليك مسؤولية التحقق من المتطلبات الحالية قبل اتخاذ القرارات."] },
        { h: "حدود المسؤولية", body: ["إلى أقصى حد يسمح به القانون، لا تتحمّل Alvin Kumar & Associates (trading as AK Fiji) أي مسؤولية عن أي خسارة تنشأ عن الاعتماد على المعلومات العامة المقدّمة في هذا الموقع."] },
      ],
      contactHeading: "التواصل",
      contactLead: "أسئلة حول هذه الشروط؟ تواصل معنا عبر:",
    },
  },
};
